import { create } from 'zustand';

import {
  type ElectionDecision,
  type ElectionLineInput,
  type SelectionContext,
  type SelectionDependent,
  type SelectionOffering,
  type SelectionQuote,
} from '../api/selection';
import { MEDICAL_WAIVE_CERTIFICATION } from '../constants/copy';

/**
 * Working state for the W2 employee self-selection flow (WP6), mirroring the
 * `store/w2-mode.ts` zustand pattern. It holds the per-offered-line decision the
 * employee is building, plus the server-returned quote.
 *
 * Who's-covered is a set of PEOPLE (the employee + named dependents, per the
 * client screen copy) — the numeric `insurance_prices_coverage_enum` tier the §4
 * API expects is DERIVED from that set (`deriveCoverage`), mirroring the B2C
 * `calculateCoverageLevel` mapping (1 employee, 2 +spouse, 3 +children, 4 family).
 *
 * Core invariant (scope §Waive model: "explicit per-line answers, never
 * absence-as-answer"): the Quote screen only unlocks once EVERY offered line
 * carries a COMPLETE decision — Elected with at least one covered person, or
 * Waived (the medical/Health waive additionally requiring the certification).
 */

/** Person id in the who's-covered set: the employee, or a dependent by index. */
export const YOU_PERSON_ID = 'you';
export const depPersonId = (index: number): string => `dep-${index}`;

/** The medical/Health category is the only waive that carries a certification. */
export const MEDICAL_CATEGORY = 'Medical';
export const isMedicalCategory = (category: string): boolean =>
  category === MEDICAL_CATEGORY;

export interface LineState {
  decision: ElectionDecision | null;
  /** Covered people (Elected): `YOU_PERSON_ID` + `dep-<i>`. Empty when Waived. */
  covered: string[];
  /** Certification text sent for a Waived MEDICAL line (empty otherwise). */
  certificationText: string;
  /** The medical-waive certification checkbox — gates a Waived medical line. */
  certified: boolean;
}

interface SelectionState {
  context: SelectionContext | null;
  /** Per-plan working decision, keyed by `planId`. */
  lines: Record<number, LineState>;
  /** The last server-computed display quote (from `saveElections`). */
  quote: SelectionQuote | null;

  // --- Medical (Health) is a SINGLE-CHOICE category ---------------------------
  // Unlike the per-plan ancillary categories, the whole Health step is ONE
  // decision: who's covered (asked once, `YOU_PERSON_ID` is implicit/always in
  // the set) + EXACTLY ONE chosen medical plan — or a single global waive that
  // declines the whole category (the only place the certification appears). These
  // four fields are the UI's source of truth; the medical entries in `lines` are
  // a pure projection of them (kept in sync by the medical actions), so the
  // generic quote/serialization consumers keep reading `lines` unchanged.
  /** Covered people for the chosen medical plan (always includes `YOU_PERSON_ID`). */
  medicalCovered: string[];
  /** The one chosen medical `planId` (null = none chosen yet). */
  medicalChoice: number | null;
  /** The global "I waive health coverage" — waives the WHOLE medical category. */
  medicalWaived: boolean;
  /** The global health-waive certification checkbox (gates a global waive). */
  medicalCertified: boolean;

  hydrate: (context: SelectionContext) => void;
  reset: () => void;

  /** Toggle a covered person on an Elected line (checking any person elects it). */
  togglePerson: (planId: number, personId: string, covered: boolean) => void;
  /** Waive the line (clears the covered set). */
  waive: (planId: number) => void;
  /** The medical-waive certification checkbox. */
  setCertified: (planId: number, certified: boolean) => void;

  /** Toggle a DEPENDENT on the shared Health who's-covered set (re-prices). */
  setMedicalDependent: (personId: string, covered: boolean) => void;
  /** Choose the one medical plan (deselects any other, clears the global waive). */
  chooseMedicalPlan: (planId: number) => void;
  /** Waive the WHOLE Health category (clears the chosen plan). */
  waiveMedicalCategory: () => void;
  /** The global health-waive certification checkbox. */
  setMedicalCertified: (certified: boolean) => void;

  setQuote: (quote: SelectionQuote) => void;

  /** Derived who's-covered tier for a line, or null when nothing is covered. */
  coverageFor: (planId: number) => number | null;
  /** Derived who's-covered tier for the Health step (from `medicalCovered`). */
  medicalCoverage: () => number | null;
  isLineComplete: (planId: number) => boolean;
  /** The single-choice Health step is complete: one plan chosen, OR globally
   * waived AND certified. */
  isMedicalComplete: () => boolean;
  isCategoryComplete: (category: string) => boolean;
  isAllComplete: () => boolean;
  toElectionInputs: () => ElectionLineInput[];
}

/** True when a dependent is the spouse (vs a child/other dependent). */
function isSpouse(dep: SelectionDependent | undefined): boolean {
  return /spouse/i.test(dep?.relationship ?? '');
}

/**
 * Map a covered-people set to the numeric `insurance_prices_coverage_enum` tier
 * (the primary/employee is always implied when anyone is covered):
 * 1 employee-only, 2 +spouse, 3 +child(ren), 4 family.
 */
export function deriveCoverage(
  covered: string[],
  dependents: SelectionDependent[]
): number | null {
  if (covered.length === 0) return null;
  let hasSpouse = false;
  let hasChild = false;
  for (const id of covered) {
    const m = /^dep-(\d+)$/.exec(id);
    if (!m) continue;
    if (isSpouse(dependents[Number(m[1])])) hasSpouse = true;
    else hasChild = true;
  }
  if (hasSpouse && hasChild) return 4;
  if (hasSpouse) return 2;
  if (hasChild) return 3;
  return 1;
}

/** Reverse of `deriveCoverage` — reconstruct a covered set from a saved tier. */
function personsFromCoverage(
  coverage: number | null | undefined,
  dependents: SelectionDependent[]
): string[] {
  const covered = [YOU_PERSON_ID];
  if (coverage == null) return covered;
  dependents.forEach((dep, i) => {
    const spouse = isSpouse(dep);
    if (coverage === 4) covered.push(depPersonId(i));
    else if (coverage === 2 && spouse) covered.push(depPersonId(i));
    else if (coverage === 3 && !spouse) covered.push(depPersonId(i));
  });
  return covered;
}

function offeringFor(
  context: SelectionContext | null,
  planId: number
): SelectionOffering | undefined {
  return context?.offerings.find((o) => o.planId === planId);
}

function emptyLine(): LineState {
  return {
    decision: null,
    covered: [],
    certificationText: '',
    certified: false,
  };
}

/** The medical `planId`s in the offered set (normalized category === Medical). */
function medicalPlanIds(context: SelectionContext | null): number[] {
  return (context?.offerings ?? [])
    .filter((o) => isMedicalCategory(o.category))
    .map((o) => o.planId);
}

/**
 * Project the single-choice Health state onto the per-plan `lines` rows the
 * quote/serialization consumers read. For each offered medical plan:
 *   • global waive  → Waived + the certification on EVERY medical line (today's
 *     convention — the whole category was declined by the user);
 *   • a plan chosen → the chosen plan Elected with the shared covered set; every
 *     OTHER medical plan Waived WITHOUT certification (auto-waived, NOT a user
 *     decline — it never showed the certification, so it carries none);
 *   • neither       → undecided (empty) medical lines.
 */
function projectMedicalLines(
  context: SelectionContext | null,
  medical: {
    covered: string[];
    choice: number | null;
    waived: boolean;
    certified: boolean;
  }
): Record<number, LineState> {
  const out: Record<number, LineState> = {};
  for (const planId of medicalPlanIds(context)) {
    if (medical.waived) {
      out[planId] = {
        decision: 'Waived',
        covered: [],
        certificationText: MEDICAL_WAIVE_CERTIFICATION,
        certified: medical.certified,
      };
    } else if (medical.choice != null) {
      out[planId] =
        planId === medical.choice
          ? {
              decision: 'Elected',
              covered: [...medical.covered],
              certificationText: '',
              certified: false,
            }
          : {
              decision: 'Waived',
              covered: [],
              certificationText: '',
              certified: false,
            };
    } else {
      out[planId] = emptyLine();
    }
  }
  return out;
}

export const useSelectionStore = create<SelectionState>((set, get) => ({
  context: null,
  lines: {},
  quote: null,
  medicalCovered: [YOU_PERSON_ID],
  medicalChoice: null,
  medicalWaived: false,
  medicalCertified: false,

  hydrate: (context) => {
    const lines: Record<number, LineState> = {};
    // ANCILLARY lines: per-plan, rehydrated from any saved-but-not-signed
    // decision so a returning employee resumes mid-flow. (Medical is rebuilt
    // below from the single-choice fields it derives.)
    for (const offering of context.offerings) {
      if (isMedicalCategory(offering.category)) continue;
      const saved = context.elections.find((e) => e.planId === offering.planId);
      if (saved?.decision === 'Elected') {
        lines[offering.planId] = {
          decision: 'Elected',
          covered: personsFromCoverage(saved.coverage, context.dependents),
          certificationText: '',
          certified: false,
        };
      } else if (saved?.decision === 'Waived') {
        lines[offering.planId] = {
          decision: 'Waived',
          covered: [],
          certificationText: '',
          certified: false,
        };
      } else {
        lines[offering.planId] = emptyLine();
      }
    }

    // MEDICAL: derive the single-choice fields from any saved medical elections
    // (elected plan → that choice + its tier; all-waived-with-certification →
    // the global waive), then project them onto the medical `lines` rows.
    const savedMedical = medicalPlanIds(context).map((planId) =>
      context.elections.find((e) => e.planId === planId)
    );
    let medicalCovered = [YOU_PERSON_ID];
    let medicalChoice: number | null = null;
    let medicalWaived = false;
    let medicalCertified = false;
    const electedMedical = savedMedical.find((e) => e?.decision === 'Elected');
    if (electedMedical) {
      medicalChoice = electedMedical.planId;
      medicalCovered = personsFromCoverage(
        electedMedical.coverage,
        context.dependents
      );
    } else if (
      savedMedical.some(
        (e) => e?.decision === 'Waived' && !!e.certificationText
      )
    ) {
      medicalWaived = true;
      medicalCertified = true;
    }
    Object.assign(
      lines,
      projectMedicalLines(context, {
        covered: medicalCovered,
        choice: medicalChoice,
        waived: medicalWaived,
        certified: medicalCertified,
      })
    );

    set({
      context,
      lines,
      quote: null,
      medicalCovered,
      medicalChoice,
      medicalWaived,
      medicalCertified,
    });
  },

  reset: () =>
    set({
      context: null,
      lines: {},
      quote: null,
      medicalCovered: [YOU_PERSON_ID],
      medicalChoice: null,
      medicalWaived: false,
      medicalCertified: false,
    }),

  togglePerson: (planId, personId, covered) =>
    set((s) => {
      const line = s.lines[planId] ?? emptyLine();
      const next = new Set(line.covered);
      if (covered) next.add(personId);
      else next.delete(personId);
      return {
        lines: {
          ...s.lines,
          [planId]: {
            ...line,
            decision: 'Elected',
            covered: [...next],
            certificationText: '',
            certified: false,
          },
        },
        // A changed decision invalidates any stale quote.
        quote: null,
      };
    }),

  waive: (planId) =>
    set((s) => {
      const medical = isMedicalCategory(
        offeringFor(s.context, planId)?.category ?? ''
      );
      const line = s.lines[planId] ?? emptyLine();
      return {
        lines: {
          ...s.lines,
          [planId]: {
            ...line,
            decision: 'Waived',
            covered: [],
            certificationText: medical ? MEDICAL_WAIVE_CERTIFICATION : '',
            // Non-medical waives need no certification, so they are complete
            // immediately; the medical waive gates on its checkbox.
            certified: line.certified && medical ? line.certified : false,
          },
        },
        quote: null,
      };
    }),

  setCertified: (planId, certified) =>
    set((s) => ({
      lines: {
        ...s.lines,
        [planId]: { ...(s.lines[planId] ?? emptyLine()), certified },
      },
    })),

  setMedicalDependent: (personId, covered) =>
    set((s) => {
      // YOU is implicit and can never leave the set; only dependents toggle.
      if (personId === YOU_PERSON_ID) return {};
      const next = new Set(s.medicalCovered);
      next.add(YOU_PERSON_ID);
      if (covered) next.add(personId);
      else next.delete(personId);
      const medicalCovered = [...next];
      return {
        medicalCovered,
        lines: {
          ...s.lines,
          ...projectMedicalLines(s.context, {
            covered: medicalCovered,
            choice: s.medicalChoice,
            waived: s.medicalWaived,
            certified: s.medicalCertified,
          }),
        },
        quote: null,
      };
    }),

  chooseMedicalPlan: (planId) =>
    set((s) => {
      const medical = {
        covered: s.medicalCovered,
        choice: planId,
        waived: false,
        certified: s.medicalCertified,
      };
      return {
        medicalChoice: planId,
        medicalWaived: false,
        lines: { ...s.lines, ...projectMedicalLines(s.context, medical) },
        quote: null,
      };
    }),

  waiveMedicalCategory: () =>
    set((s) => {
      const medical = {
        covered: s.medicalCovered,
        choice: null,
        waived: true,
        certified: s.medicalCertified,
      };
      return {
        medicalWaived: true,
        medicalChoice: null,
        lines: { ...s.lines, ...projectMedicalLines(s.context, medical) },
        quote: null,
      };
    }),

  setMedicalCertified: (certified) =>
    set((s) => {
      const medical = {
        covered: s.medicalCovered,
        choice: s.medicalChoice,
        waived: s.medicalWaived,
        certified,
      };
      return {
        medicalCertified: certified,
        lines: { ...s.lines, ...projectMedicalLines(s.context, medical) },
      };
    }),

  setQuote: (quote) => set({ quote }),

  coverageFor: (planId) => {
    const { context, lines } = get();
    const line = lines[planId];
    if (!line || line.decision !== 'Elected') return null;
    return deriveCoverage(line.covered, context?.dependents ?? []);
  },

  medicalCoverage: () => {
    const { context, medicalCovered } = get();
    return deriveCoverage(medicalCovered, context?.dependents ?? []);
  },

  isLineComplete: (planId) => {
    const { context, lines } = get();
    const line = lines[planId];
    if (!line || !line.decision) return false;
    if (line.decision === 'Elected') return line.covered.length > 0;
    // Waived: the medical/Health line requires its certification; others don't.
    const medical = isMedicalCategory(
      offeringFor(context, planId)?.category ?? ''
    );
    return medical ? line.certified : true;
  },

  isMedicalComplete: () => {
    const { context, medicalWaived, medicalCertified, medicalChoice } = get();
    if (!context) return false;
    // No medical offered → nothing to complete for the Health step.
    if (medicalPlanIds(context).length === 0) return true;
    if (medicalWaived) return medicalCertified;
    // A chosen plan always carries at least YOU (implicit) → a valid tier.
    return medicalChoice != null;
  },

  isCategoryComplete: (category) => {
    const { context } = get();
    if (!context) return false;
    // The Health step is single-choice — completeness is category-level, not
    // per-line (its auto-waived non-chosen plans carry no certification).
    if (isMedicalCategory(category)) return get().isMedicalComplete();
    return context.offerings
      .filter((o) => o.category === category)
      .every((o) => get().isLineComplete(o.planId));
  },

  isAllComplete: () => {
    const { context } = get();
    if (!context || context.offerings.length === 0) return false;
    if (!get().isMedicalComplete()) return false;
    return context.offerings
      .filter((o) => !isMedicalCategory(o.category))
      .every((o) => get().isLineComplete(o.planId));
  },

  toElectionInputs: () => {
    const { context, lines } = get();
    if (!context) return [];
    return context.offerings.map((o): ElectionLineInput => {
      const line = lines[o.planId];
      if (line?.decision === 'Elected') {
        return {
          planId: o.planId,
          decision: 'Elected',
          coverage:
            deriveCoverage(line.covered, context.dependents) ?? undefined,
        };
      }
      // Waived. Emit the certification text ONLY when the line actually carries
      // it — the global Health waive stamps it on every medical line; an
      // auto-waived non-chosen medical plan and every ancillary waive carry none
      // (the backend still stamps `certifiedAt` per line).
      const certificationText = line?.certificationText;
      return certificationText
        ? { planId: o.planId, decision: 'Waived', certificationText }
        : { planId: o.planId, decision: 'Waived' };
    });
  },
}));
